import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createContract, addMilestone } from "@/services/contracts.service";
import { getKubPrice } from "@/services/pricing.service";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft, ArrowRight, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { fmtMoney } from "@/lib/format";
import { NATIVE_TOKEN, PAYMENT_OPTIONS } from "@/lib/tokens";
import { useEmailLookup } from "@/lib/useEmailLookup";

const CreateContract = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", total_amount: "", currency: "USD", start_date: "", end_date: "",
    payee_email: "",
    my_role: "payer", // "payer" = I'm paying, "payee" = I'm getting paid
  });
  const [milestones, setMilestones] = useState([{ title: "", amount: "", due_date: "", notes: "" }]);
  const [payMode, setPayMode] = useState("usdc");
  const [kubPrice, setKubPrice] = useState(0);

  useEffect(() => { getKubPrice().then((p) => setKubPrice(p?.usd || 0)).catch(() => {}); }, []);

  const total = milestones.reduce((s, m) => s + (parseFloat(m.amount) || 0), 0);
  const isKub = payMode === "kub";
  const emailCheck = useEmailLookup(form.payee_email);
  const emailLooksValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test((form.payee_email || "").trim());
  const payerMustExist = form.my_role === "payee";
  const emailBlocks = emailCheck.self || (payerMustExist && emailLooksValid && !emailCheck.checking && !emailCheck.exists);
  const inKub = (usd) => (kubPrice > 0 ? usd / kubPrice : 0);
  const today = new Date().toLocaleDateString("en-CA");
  const floor = form.start_date || today;

  const startErr = form.start_date && form.start_date < today ? "Start date can't be in the past" : "";
  const endErr =
    form.end_date && form.end_date < floor
      ? form.start_date
        ? "End date can't be before the start date"
        : "End date can't be in the past"
      : "";
  const dueErr = (m) =>
    m.due_date && m.due_date < floor ? "Due date can't be before the start date" : "";
  const firstDateError = startErr || endErr || milestones.map(dueErr).find(Boolean) || null;

  const addMs = () => setMilestones([...milestones, { title: "", amount: "", due_date: "", notes: "" }]);
  const rmMs = (i) => setMilestones(milestones.filter((_, idx) => idx !== i));
  const updMs = (i, k, v) => setMilestones(milestones.map((m, idx) => idx === i ? { ...m, [k]: v } : m));

  const submit = async () => {
    if (!form.payee_email) { toast.error("Counterparty email required"); return; }
    if (emailCheck.self) { toast.error("You can't create a contract with yourself"); return; }
    if (payerMustExist && emailLooksValid && !emailCheck.exists) {
      toast.error("The payer must have a PAYD account. Ask them to sign up first.");
      return;
    }
    if (milestones.some((m) => !m.title || !m.amount)) { toast.error("Each milestone needs a title and amount"); return; }
    if (firstDateError) { toast.error(firstDateError); return; }
    setSaving(true);
    try {
      const payload = { ...form, total_amount: total, payment_token: isKub ? NATIVE_TOKEN : undefined };
      const data = await createContract(payload);
      for (const m of milestones) {
        await addMilestone(data.contract_id, {
          title: m.title, amount: parseFloat(m.amount), due_date: m.due_date || null, notes: m.notes,
        });
      }
      toast.success("Contract created");
      navigate(`/contracts/${data.contract_id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const stepLabels = ["Role & info", "Milestones", "Invite counterparty"];

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="create-contract-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">New contract</div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Create a contract</h1>
      </div>

      <div className="flex items-center gap-3">
        {stepLabels.map((label, i) => {
          const n = i + 1;
          return (
            <div key={n} className={`flex items-center gap-2 ${n <= step ? "text-foreground" : "text-muted-foreground"}`}>
              <div className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold ${n <= step ? "bg-primary text-primary-foreground" : "bg-secondary"}`}>{n}</div>
              <span className="text-sm font-medium hidden sm:inline">{label}</span>
              {n < 3 && <div className={`w-8 h-px ${n < step ? "bg-primary" : "bg-border"}`} />}
            </div>
          );
        })}
      </div>

      <div className="bg-card border border-border rounded-xl p-6 sm:p-8 space-y-6">
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <Label>Your role on this contract</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1.5">
                {[
                  { v: "payer", l: "I'm paying", hint: "You'll fund escrow and approve work." },
                  { v: "payee", l: "I'm getting paid", hint: "You'll submit work and receive funds." },
                ].map((o) => (
                  <button
                    key={o.v}
                    type="button"
                    onClick={() => setForm({ ...form, my_role: o.v })}
                    className={`text-left p-4 rounded-lg border transition-colors ${form.my_role === o.v ? "border-primary bg-accent" : "border-border hover:bg-secondary/50"}`}
                    data-testid={`role-${o.v}-btn`}
                  >
                    <div className="font-display font-semibold">{o.l}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{o.hint}</div>
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Settlement token</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1.5">
                {PAYMENT_OPTIONS.map((o) => (
                  <button
                    key={o.mode}
                    type="button"
                    onClick={() => setPayMode(o.mode)}
                    className={`text-left p-4 rounded-lg border transition-colors ${payMode === o.mode ? "border-primary bg-accent" : "border-border hover:bg-secondary/50"}`}
                    data-testid={`token-${o.mode}-btn`}
                  >
                    <div className="font-display font-semibold">{o.label}</div>
                    <div className="text-xs text-muted-foreground mt-0.5">{o.hint}</div>
                  </button>
                ))}
              </div>
              {isKub && (
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Amounts are set in USD and funded in KUB at the rate when each milestone is funded
                  {kubPrice > 0 ? ` (~$${kubPrice.toFixed(4)}/KUB now)` : ""}.
                </p>
              )}
            </div>
            <div>
              <Label>Title *</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Brand redesign" className="mt-1.5 h-11" data-testid="contract-title-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={4} className="mt-1.5" placeholder="Scope of work…" data-testid="contract-desc-input" />
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Start date</Label>
                <Input type="date" min={today} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="mt-1.5 h-11" aria-invalid={!!startErr} data-testid="contract-start-input" />
                {startErr && <p className="mt-1 text-xs text-red-600" data-testid="contract-start-error">{startErr}</p>}
              </div>
              <div>
                <Label>End date</Label>
                <Input type="date" min={form.start_date || today} value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} className="mt-1.5 h-11" aria-invalid={!!endErr} data-testid="contract-end-input" />
                {endErr && <p className="mt-1 text-xs text-red-600" data-testid="contract-end-error">{endErr}</p>}
              </div>
            </div>
          </div>
        )}
        {step === 2 && (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <div className="font-display font-semibold text-lg">Milestones</div>
                <div className="text-sm text-muted-foreground">Break work into fundable chunks.</div>
              </div>
              <div className="text-right">
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Total</div>
                <div className="font-display font-bold text-xl">{fmtMoney(total)}</div>
                {isKub && kubPrice > 0 && <div className="text-xs text-muted-foreground">≈ {inKub(total).toFixed(2)} KUB</div>}
              </div>
            </div>
            <div className="space-y-3">
              {milestones.map((m, i) => (
                <div key={i} className="border border-border rounded-lg p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Milestone {i + 1}</div>
                    {milestones.length > 1 && (
                      <Button type="button" size="icon" variant="ghost" onClick={() => rmMs(i)} data-testid={`remove-milestone-${i}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Input placeholder="Title" value={m.title} onChange={(e) => updMs(i, "title", e.target.value)} className="h-11" data-testid={`milestone-title-${i}`} />
                    <Input type="number" placeholder="Amount" value={m.amount} onChange={(e) => updMs(i, "amount", e.target.value)} className="h-11" data-testid={`milestone-amount-${i}`} />
                    <div>
                      <Input type="date" min={form.start_date || today} placeholder="Due date" value={m.due_date} onChange={(e) => updMs(i, "due_date", e.target.value)} className="h-11 w-full" aria-invalid={!!dueErr(m)} data-testid={`milestone-due-${i}`} />
                      {dueErr(m) && <p className="mt-1 text-xs text-red-600" data-testid={`milestone-due-error-${i}`}>{dueErr(m)}</p>}
                    </div>
                    <Input placeholder="Notes (optional)" value={m.notes} onChange={(e) => updMs(i, "notes", e.target.value)} className="h-11" data-testid={`milestone-notes-${i}`} />
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" variant="outline" onClick={addMs} className="w-full" data-testid="add-milestone-btn">
              <Plus className="w-4 h-4 mr-1.5" /> Add milestone
            </Button>
          </div>
        )}
        {step === 3 && (
          <div className="space-y-4">
            <div>
              <Label>Counterparty email *</Label>
              <Input type="email" value={form.payee_email} onChange={(e) => setForm({ ...form, payee_email: e.target.value })} placeholder={form.my_role === "payer" ? "payee@example.com" : "payer@example.com"} className="mt-1.5 h-11" data-testid="freelancer-email-input" />
              {!emailLooksValid ? (
                <p className="mt-2 text-xs text-muted-foreground">
                  {form.my_role === "payer"
                    ? "The person you'll pay. If they're not on PAYD yet they'll be invited."
                    : "The person who will pay you. They must have a PAYD account to be set up as the payer."}
                </p>
              ) : emailCheck.checking ? (
                <p className="mt-2 text-xs text-muted-foreground inline-flex items-center gap-1.5" data-testid="email-check"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking…</p>
              ) : emailCheck.self ? (
                <p className="mt-2 text-xs text-red-500 inline-flex items-center gap-1.5" data-testid="email-check"><AlertCircle className="w-3.5 h-3.5" /> That's your own email.</p>
              ) : emailCheck.exists ? (
                <p className="mt-2 text-xs text-primary inline-flex items-center gap-1.5" data-testid="email-check"><CheckCircle2 className="w-3.5 h-3.5" /> {emailCheck.name || "This user"} is on PAYD.</p>
              ) : payerMustExist ? (
                <p className="mt-2 text-xs text-red-500 inline-flex items-center gap-1.5" data-testid="email-check"><AlertCircle className="w-3.5 h-3.5" /> No PAYD account found. The payer must sign up first.</p>
              ) : (
                <p className="mt-2 text-xs text-amber-500 inline-flex items-center gap-1.5" data-testid="email-check"><AlertCircle className="w-3.5 h-3.5" /> Not on PAYD yet. They'll get an email invite.</p>
              )}
            </div>
            <div className="bg-accent/40 border border-border rounded-lg p-4">
              <div className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">Summary</div>
              <div className="mt-2 space-y-1 text-sm">
                <div className="flex justify-between"><span className="text-muted-foreground">Your role</span><span className="font-medium">{form.my_role === "payer" ? "PAYER (I'm paying)" : "PAYEE (I'm getting paid)"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Title</span><span className="font-medium">{form.title || "—"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Milestones</span><span className="font-medium">{milestones.length}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Settle in</span><span className="font-medium">{isKub ? "KUB" : "USDC"}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">Total</span><span className="font-display font-bold">{fmtMoney(total)}{isKub && kubPrice > 0 ? ` ≈ ${inKub(total).toFixed(2)} KUB` : ""}</span></div>
              </div>
            </div>
          </div>
        )}

        <div className="flex items-center justify-between pt-4 border-t border-border">
          <Button type="button" variant="ghost" onClick={() => setStep(Math.max(1, step - 1))} disabled={step === 1} data-testid="wizard-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1.5" /> Back
          </Button>
          {step < 3 ? (
            <Button type="button" onClick={() => {
              if (step === 1 && !form.title) { toast.error("Title required"); return; }
              if (step === 2 && total <= 0) { toast.error("Add at least one milestone with amount"); return; }
              if (firstDateError) { toast.error(firstDateError); return; }
              setStep(step + 1);
            }} disabled={!!firstDateError} data-testid="wizard-next-btn">
              Continue <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          ) : (
            <Button type="button" onClick={submit} disabled={saving || !!firstDateError || emailBlocks} data-testid="wizard-submit-btn">
              {saving ? "Creating…" : "Create contract"}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateContract;
